import { isValidPhoneNumber, parseCountry } from "lopermut"
import { z, ZodType } from "zod"
import { Errors } from "../errors"

type Filter = {
  key: string
  value: ZodType
}

type FilterRecord<T extends readonly Filter[]> = {
  [K in T[number]["key"]]: Extract<T[number], { key: K }>["value"]
}

export function createFilterBySchema<T extends readonly Filter[]>(filters: T) {
  return z.object({
    filterBy: z
      .object(
        filters.reduce((acc, filter) => {
          acc[filter.key as T[number]["key"]] = filter.value
          return acc
        }, {} as FilterRecord<T>),
      )
      .partial()
      .optional(),
  })
}

export function oneOrMany<T extends ZodType>(schema: T) {
  return z
    .union([schema, z.array(schema)])
    .transform((value) => (Array.isArray(value) ? value : [value]))
    .optional()
}

export const CountryFilterSchema = z
  .string()
  .optional()
  .nullable()
  .refine((value) => !value || parseCountry(value), {
    message: Errors.INVALID_COUNTRY,
    path: ["country"],
  })

export const FromToFilterSchema = z.object({
  from: z.string().optional().refine(_validateDate),
  to: z.string().optional().refine(_validateDate),
})

function _validateDate(value: string | undefined) {
  if (!value) return true
  const date = new Date(value)
  return !isNaN(date.getTime())
}

export function validatePhoneNumber(
  value: string | null | undefined,
  countryCode?: string | null,
  locale?: string | null,
) {
  if (!value) return true
  return isValidPhoneNumber(value, countryCode ?? undefined, locale ?? undefined)
}

export const DateRangeSchema = z
  .union([
    z.object({
      from: z.string(),
      to: z.string().optional(),
    }),
    z.object({
      from: z.date(),
      to: z.date().optional(),
    }),
  ])
  .optional()
  .transform((data) => {
    if (!data) return undefined
    if (!data.from) return undefined
    return {
      from: new Date(data.from),
      to: data.to ? new Date(data.to) : undefined,
    }
  })
